import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingBag, MapPin, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import StarRow from "../components/StarRow";

const API = process.env.REACT_APP_BACKEND_URL;

const TestimonialsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    reviewer_name: "",
    rating: 5,
    title: "",
    body: "",
    location: "",
    email: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);
  const [error, setError] = useState(null);

  const fetchItems = () => {
    setLoading(true);
    axios
      .get(`${API}/api/testimonials`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(fetchItems, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitOk(false);
    const trimmedName = form.reviewer_name.trim();
    const trimmedBody = form.body.trim();
    if (!trimmedName || !trimmedBody) {
      setError("Please share your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/testimonials`, {
        reviewer_name: trimmedName,
        rating: parseInt(form.rating, 10) || 5,
        title: form.title.trim() || null,
        body: trimmedBody,
        location: form.location.trim() || null,
        email: form.email.trim() || null
      });
      setSubmitOk(true);
      setForm({ reviewer_name: "", rating: 5, title: "", body: "", location: "", email: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating = items.length > 0
    ? (items.reduce((s, t) => s + (t.rating || 0), 0) / items.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-[#E9C46A]/25 via-[#457B9D]/15 to-[#2A9D8F]/20 border-b-4 border-[#1D3557]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#E63946] font-black uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            <MessageCircle className="inline w-5 h-5 mb-1 mr-1" />
            Reader reviews
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Loved by farm friends around the world
          </h1>
          <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
            Read what parents, teachers, librarians and little readers have said about Callie&apos;s adventures — and share your own.
          </p>
          {avgRating && (
            <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-2xl px-5 py-3 border-3 border-[#1D3557] shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]" data-testid="avg-rating">
              <StarRow value={Math.round(avgRating)} size={22} />
              <span className="font-black text-[#1D3557] text-lg" style={{ fontFamily: "'Fredoka', sans-serif" }}>{avgRating} / 5</span>
              <span className="text-[#1D3557]/70 font-medium text-sm">from {items.length} review{items.length === 1 ? "" : "s"}</span>
            </div>
          )}
        </div>
      </section>

      {/* Review list */}
      <section className="py-14 px-4 md:px-8 max-w-6xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-[#1D3557]/70 font-medium">
            Be the first to leave a review — use the form below!
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-grid">
            {items.map((t, i) => (
              <motion.article
                key={t.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="tactile-card p-6 bg-white"
                data-testid={`review-card-${t.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <StarRow value={t.rating} size={20} />
                  {t.source === "amazon" && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FF9900] text-[#1D3557] text-xs font-black border-2 border-[#1D3557]">
                      <ShoppingBag className="w-3 h-3" />
                      Amazon
                    </span>
                  )}
                </div>
                {t.title && (
                  <h3 className="font-black text-lg text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {t.title}
                  </h3>
                )}
                <p className="text-[#1D3557] font-medium leading-relaxed whitespace-pre-line">
                  {t.body}
                </p>
                <div className="mt-4 pt-3 border-t-2 border-[#1D3557]/10 flex items-center justify-between flex-wrap gap-2 text-sm">
                  <span className="font-bold text-[#1D3557]">— {t.reviewer_name}</span>
                  {t.location && (
                    <span className="text-[#1D3557]/70 font-medium inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {t.location}
                    </span>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {/* Submit form */}
      <section className="py-14 px-4 md:px-8 bg-[#1D3557]" id="submit-review">
        <div className="max-w-2xl mx-auto">
          <div className="text-center text-white mb-8">
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Share your review
            </h2>
            <p className="text-white/85 font-medium">
              Tell other farm friends what you thought of Callie&apos;s adventures. Your review will appear here after a quick check.
            </p>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-6 md:p-8 space-y-5 border-4 border-[#E9C46A] shadow-[6px_6px_0px_0px_rgba(233,196,106,1)]"
            data-testid="review-submit-form"
          >
            <div>
              <label className="block font-bold text-[#1D3557] mb-2">Your rating *</label>
              <StarRow value={form.rating} size={30} interactive onChange={(v) => setForm((f) => ({ ...f, rating: v }))} testIdPrefix="rating-star" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-[#1D3557] mb-1">Your name *</label>
                <input
                  name="reviewer_name"
                  value={form.reviewer_name}
                  onChange={handleChange}
                  required
                  maxLength={120}
                  className="input-field"
                  data-testid="reviewer-name-input"
                />
              </div>
              <div>
                <label className="block font-bold text-[#1D3557] mb-1">Location <span className="text-[#1D3557]/50 font-normal text-sm">(optional, shown publicly)</span></label>
                <input
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  maxLength={120}
                  placeholder="e.g. Bridgetown, Barbados"
                  className="input-field"
                  data-testid="reviewer-location-input"
                />
              </div>
            </div>

            <div>
              <label className="block font-bold text-[#1D3557] mb-1">Review title <span className="text-[#1D3557]/50 font-normal text-sm">(optional)</span></label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                maxLength={200}
                placeholder="e.g. My daughter loved it!"
                className="input-field"
                data-testid="reviewer-title-input"
              />
            </div>

            <div>
              <label className="block font-bold text-[#1D3557] mb-1">Your review *</label>
              <textarea
                name="body"
                value={form.body}
                onChange={handleChange}
                required
                rows="4"
                maxLength={4000}
                className="input-field"
                placeholder="What did you (or your little reader) enjoy most?"
                data-testid="reviewer-body-input"
              />
              <p className="text-xs text-[#1D3557]/50 mt-1">{form.body.length}/4000 characters</p>
            </div>

            <div>
              <label className="block font-bold text-[#1D3557] mb-1">Email <span className="text-[#1D3557]/50 font-normal text-sm">(optional, kept private)</span></label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="input-field"
                placeholder="only if you're happy for us to follow up"
                data-testid="reviewer-email-input"
              />
            </div>

            {error && (
              <div className="bg-[#E63946]/10 border-2 border-[#E63946] rounded-xl p-3 flex items-center gap-2" data-testid="submit-error">
                <AlertCircle className="w-5 h-5 text-[#E63946]" />
                <p className="text-[#E63946] font-medium text-sm">{error}</p>
              </div>
            )}
            {submitOk && (
              <div className="bg-[#2A9D8F]/10 border-2 border-[#2A9D8F] rounded-xl p-3 flex items-center gap-2" data-testid="submit-success">
                <CheckCircle2 className="w-5 h-5 text-[#2A9D8F]" />
                <p className="text-[#1D3557] font-bold text-sm">
                  Thanks! Your review is queued for a quick approval and will appear here shortly.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="submit-review-btn"
            >
              <Send className="w-5 h-5" />
              {submitting ? "Sending…" : "Submit review"}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
